import { motion } from "framer-motion";
import { Apple, Download, Gamepad2, Play, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useLang, STR } from "@/i18n";

export const DEFAULT_WEB_GAME_URL = "https://credit-clash.preview.emergentagent.com";
export const APK_DOWNLOAD_URL = "https://api.expo.dev/v2/artifacts/eas/bkN4zx0P4Rzip9nQOFrRV5KkQ5TWnkqpscmUzSy_Lr0";

export const pwaLabels = {
  pt: {
    openOverline: "TESTAR AGORA",
    openButton: "Abrir Game",
    openToast: "Abrindo o game online.",
    apkOverline: "APK para Android",
    apkButton: "Baixar Game",
    qrTitle: "TESTE AGORA",
    qrSub: "Escaneie o QR Code, jogue pelo celular e conte pra gente o que achou.",
  },
  en: {
    openOverline: "TRY IT NOW",
    openButton: "Open Game",
    openToast: "Opening the online game.",
    apkOverline: "APK for Android",
    apkButton: "Download Game",
    qrTitle: "TRY IT NOW",
    qrSub: "Scan the QR Code, play on your phone and tell us what you think.",
  },
  es: {
    openOverline: "PRUÉBALO AHORA",
    openButton: "Abrir Game",
    openToast: "Abriendo el juego online.",
    apkOverline: "APK para Android",
    apkButton: "Descargar Game",
    qrTitle: "PRUÉBALO AHORA",
    qrSub: "Escanea el código QR, juega en tu celular y cuéntanos qué te pareció.",
  },
};

export const openWebGame = () => {
  window.open(DEFAULT_WEB_GAME_URL, "_blank", "noopener,noreferrer");
};

export default function DownloadCTA() {
  const { lang } = useLang();
  const t = STR[lang];
  const pwa = pwaLabels[lang] || pwaLabels.pt;
  const stores = [
    {
      id: "cta-download-appstore-btn",
      icon: Apple,
      label: t.dl_apple,
      store: "App Store",
    },
    {
      id: "cta-download-playstore-btn",
      icon: Play,
      label: t.dl_google,
      store: "Google Play",
    },
  ];

  const notify = (store) => toast.info(t.cta_toast.replace("{store}", store));
  const handleOpenWebGame = () => {
    toast.success(pwa.openToast);
    openWebGame();
  };

  return (
    <section id="download" className="relative overflow-hidden py-24 noise-overlay sm:py-32">
      <div className="hero-grid-bg pointer-events-none absolute inset-0 opacity-60" />
      <div className="pointer-events-none absolute left-1/2 top-0 h-[24rem] w-[46rem] -translate-x-1/2 rounded-full bg-blue-600/15 blur-[140px]" />

      <div className="relative mx-auto max-w-5xl px-5 text-center sm:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-8 flex justify-center"
        >
          <img
            src="/logo.png"
            alt="DUELO"
            className="w-28 drop-shadow-[0_14px_44px_rgba(251,191,36,0.3)] sm:w-36"
          />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="font-display text-3xl font-black leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl"
        >
          {t.cta_h2a}{" "}
          <span className="bg-gradient-to-r from-sky-300 via-blue-400 to-amber-300 bg-clip-text text-transparent">
            {t.cta_h2b}
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.12 }}
          className="mx-auto mt-5 max-w-xl text-base text-slate-400 sm:text-lg"
        >
          {t.cta_sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.22 }}
          className="mx-auto mt-10 grid max-w-[560px] grid-cols-1 justify-items-center gap-5 sm:grid-cols-2"
        >
          {stores.map((store) => (
            <button
              key={store.id}
              type="button"
              onClick={() => notify(store.store)}
              data-testid={store.id}
              className="group relative flex min-h-[92px] w-full max-w-[280px] items-center gap-4 rounded-3xl border border-white/10 bg-[#0c1122]/90 px-7 py-5 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-sky-300/35 active:scale-[0.98] sm:w-[260px]"
            >
              <store.icon className={`h-9 w-9 ${store.store === "Google Play" ? "fill-amber-300 text-amber-300" : "text-white"}`} />
              <span className="text-left leading-tight">
                <span className="block text-xs text-slate-400">{store.label}</span>
                <span className="block font-display text-lg font-bold text-white">{store.store}</span>
              </span>
              <span className="mono absolute -right-2 -top-2.5 rounded-full border border-sky-400/30 bg-sky-400/10 px-2.5 py-1 text-[9px] uppercase tracking-[0.18em] text-sky-300">
                {t.badge_soon}
              </span>
            </button>
          ))}

          <a
            href={APK_DOWNLOAD_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-download-apk-btn"
            className="group flex min-h-[92px] w-full max-w-[280px] items-center gap-4 rounded-3xl border border-amber-300/30 bg-amber-300 px-7 py-5 text-[#130b25] shadow-2xl shadow-amber-300/15 transition-all duration-300 hover:-translate-y-1 hover:bg-amber-200 active:scale-[0.98] sm:w-[260px]"
          >
            <Download className="h-9 w-9" />
            <span className="text-left leading-tight">
              <span className="block text-xs font-semibold text-[#39220a]/70">{pwa.apkOverline}</span>
              <span className="block font-display text-lg font-black">{pwa.apkButton}</span>
            </span>
          </a>

          <button
            type="button"
            onClick={handleOpenWebGame}
            data-testid="cta-open-web-game-btn"
            className="group flex min-h-[92px] w-full max-w-[280px] items-center gap-4 rounded-3xl border border-lime-300/30 bg-lime-300 px-7 py-5 text-[#071018] shadow-2xl shadow-lime-300/20 transition-all duration-300 hover:-translate-y-1 hover:bg-lime-200 active:scale-[0.98] sm:w-[260px]"
          >
            <Gamepad2 className="h-9 w-9" />
            <span className="text-left leading-tight">
              <span className="block text-xs font-semibold text-[#25310f]/70">{pwa.openOverline}</span>
              <span className="block font-display text-lg font-black">{pwa.openButton}</span>
            </span>
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.32 }}
          className="mt-12 flex justify-center"
        >
          <div
            data-testid="qr-download-card"
            className="flex max-w-md flex-col items-center gap-5 rounded-3xl border border-white/10 bg-[#0c1221]/90 px-6 py-7 text-center shadow-2xl shadow-black/50 transition-all duration-300"
          >
            <div className="mx-auto shrink-0 rounded-2xl bg-white p-3">
              <QRCodeSVG
                value={DEFAULT_WEB_GAME_URL}
                size={132}
                fgColor="#06091a"
              />
            </div>
            <div className="max-w-[340px] text-center">
              <p className="flex items-center justify-center gap-2 font-display text-base font-bold text-white">
                <QrCode className="h-5 w-5 text-amber-300" />
                {pwa.qrTitle}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">
                {pwa.qrSub}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
